import { appHome, ploteaApp, productName, qaPanel } from '../lib/productProfile'
/**
 * Login + Register (shadcn, RM-117). Same testids/classes/flow:
 * login-heading, login-submit, register-submit, oauth-google/github, toggle via
 * "Zarejestruj sie"/"Zaloguj sie". EmailNotVerified → /verify-email/$email.
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthShell, NoticeBanner } from '../components/Auth'
import { DevOnly } from '../components/DevOnly'
import { BrandHeader } from '../components/Logo'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { login, register, getSeedUsers } from '../lib/endpoints/auth'
import { ApiError } from '../lib/errors'
import { qk } from '../lib/queryKeys'
import { safeLoreReturnPath } from '../lib/loreAuth'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showRegister, setShowRegister] = useState(false)
  const nav = useNavigate()
  const qc = useQueryClient()
  const returnTo = safeLoreReturnPath(
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('return_to'),
  )

  // NOTE: no mount-time redirect-when-authenticated. The Dioxus login page had
  // one, but it read cached app-level auth state; a fresh /me fetch here would
  // race the login click (mock verify-email logs the user in, so /me flips to
  // authenticated and the form unmounts mid-action). onLogin navigates to the profile home
  // on success explicitly, so the redirect is redundant for the happy path.

  // Dev-only quick-login accounts (empty list on real deployments → panel hidden).
  const seedUsers = useQuery({
    queryKey: qk.seedUsers(),
    enabled: !qaPanel && !ploteaApp,
    queryFn: getSeedUsers,
    staleTime: Infinity,
    retry: false,
  })

  const toVerify = (e: string) =>
    nav({ to: '/verify-email/$email', params: { email: e }, replace: true })

  const loginWith = async (em: string, pw: string) => {
    setError(null)
    setLoading(true)
    try {
      await login(em, pw)
      await qc.invalidateQueries({ queryKey: qk.me() })
      if (returnTo) {
        window.location.assign(returnTo)
      } else {
        await nav({ to: appHome, replace: true })
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'EmailNotVerified') {
        void toVerify(em)
      } else {
        setError(e instanceof Error ? e.message : 'Blad logowania')
      }
    } finally {
      setLoading(false)
    }
  }

  const onLogin = () => loginWith(email, password)

  const onSeedLogin = (em: string, pw: string) => {
    // Reflect the choice in the form, then sign in directly.
    setEmail(em)
    setPassword(pw)
    void loginWith(em, pw)
  }

  const onRegister = async () => {
    setError(null)
    setLoading(true)
    try {
      await register(email, password, name)
      void toVerify(email)
    } catch (e) {
      if (
        e instanceof ApiError &&
        (e.code === 'EmailAlreadyExists' || e.code === 'EmailNotVerified')
      ) {
        void toVerify(email)
      } else {
        setError(e instanceof Error ? e.message : 'Blad rejestracji')
      }
    } finally {
      setLoading(false)
    }
  }

  const verified =
    typeof window !== 'undefined' &&
    window.location.search.includes('verified=true')

  return (
    <AuthShell>
      <div className="mb-6" data-testid="login-heading">
        <BrandHeader
          topic={ploteaApp ? 'Analiza działek' : 'Agents for architecture / 01'}
          compact
          metadata={null}
        />
      </div>
      <h1 className="mb-6 text-xl font-semibold text-foreground">
        {showRegister
          ? `Utworz konto ${productName}`
          : `Zaloguj sie do ${productName}`}
      </h1>

      {verified && (
        <NoticeBanner
          message="Email zweryfikowany! Mozesz sie zalogowac."
          tone="success"
          centered
        />
      )}
      {error && <NoticeBanner message={error} tone="error" />}

      {showRegister ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            void onRegister()
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="register-name">Imie</Label>
            <Input
              id="register-name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder="Jan Kowalski"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="register-email">Email</Label>
            <Input
              id="register-email"
              name="email"
              type="email"
              autoComplete="username"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="register-password">Haslo</Label>
            <Input
              id="register-password"
              name="password"
              type="password"
              autoComplete="new-password"
              placeholder="Min. 8 znakow"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button
            className="w-full"
            size="lg"
            type="submit"
            data-testid="register-submit"
            disabled={loading}
          >
            {loading ? 'Rejestracja...' : 'Zarejestruj sie'}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Masz konto?{' '}
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() => setShowRegister(false)}
            >
              Zaloguj sie
            </Button>
          </p>
        </form>
      ) : (
        <div className="space-y-4">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              void onLogin()
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="login-email">Email</Label>
              <Input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-password">Haslo</Label>
              <Input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button
              className="w-full"
              size="lg"
              type="submit"
              data-testid="login-submit"
              disabled={loading}
            >
              {loading ? 'Logowanie...' : 'Zaloguj sie'}
            </Button>
          </form>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-card px-2 text-muted-foreground">lub</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            size="lg"
            data-testid="oauth-google"
            disabled
          >
            Google (wkrotce)
          </Button>
          <Button
            variant="outline"
            className="w-full"
            size="lg"
            data-testid="oauth-github"
            disabled
          >
            GitHub (wkrotce)
          </Button>

          <p className="text-center text-sm text-muted-foreground mt-4">
            Nie masz konta?{' '}
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() => setShowRegister(true)}
            >
              Zarejestruj sie
            </Button>
          </p>

          {(seedUsers.data?.users.length ?? 0) > 0 && (
            <div className="mt-4">
              <DevOnly label="Dev accounts">
                <div className="space-y-1.5">
                  {seedUsers.data?.users.map((u) => (
                    <Button
                      key={u.email}
                      variant="outline"
                      size="sm"
                      className="w-full justify-between bg-card font-normal hover:bg-muted dark:bg-card! dark:hover:bg-muted!"
                      disabled={loading}
                      onClick={() => onSeedLogin(u.email, u.password)}
                    >
                      <span>{u.label}</span>
                      <span className="text-muted-foreground">{u.email}</span>
                    </Button>
                  ))}
                </div>
              </DevOnly>
            </div>
          )}
        </div>
      )}
    </AuthShell>
  )
}
